import React from 'react';
import { StyleSheet, View } from 'react-native';
import Icon, { IconKeyType } from 'shared/components/vxl/icon/Icon/Icon';
import BodyXSText from 'shared/components/vxl/typography/primary/body/BodyXSText';
import theme from 'shared/styles/theme';
import { Maybe } from 'shared/utils/types';

export enum TagType {
  INFO,
  INFO_EMPHASIZED,
  INFO_REVERSE,
  SUCCESS,
  WARNING,
  ERROR,
  HIGHLIGHT,
}

export const tagTypeToStyles: Record<
  TagType,
  { backgroundColor: string; contentColor: string }
> = {
  [TagType.INFO]: {
    backgroundColor: theme.color.background.secondary,
    contentColor: theme.color.text.primary,
  },
  [TagType.INFO_EMPHASIZED]: {
    backgroundColor: theme.color.status.info,
    contentColor: theme.color.text.inverse,
  },
  [TagType.INFO_REVERSE]: {
    backgroundColor: theme.color.background.primary,
    contentColor: theme.color.text.primary,
  },
  [TagType.SUCCESS]: {
    backgroundColor: theme.color.status.positiveLight,
    contentColor: theme.color.status.positiveDark,
  },
  [TagType.WARNING]: {
    backgroundColor: theme.color.status.warning,
    contentColor: theme.color.text.primary,
  },
  [TagType.ERROR]: {
    backgroundColor: theme.color.status.negative,
    contentColor: theme.color.text.inverse,
  },
  [TagType.HIGHLIGHT]: {
    backgroundColor: theme.color.expressive.lime,
    contentColor: theme.color.text.primary,
  },
};

export interface TagProps {
  text: string;
  type: TagType;
  iconKey?: Maybe<IconKeyType>;
}

export const Tag = ({ text, type, iconKey = undefined }: TagProps) => {
  const { backgroundColor, contentColor } = tagTypeToStyles[type];

  return (
    <View
      testID="tag-container"
      style={[styles.container, { backgroundColor }]}
    >
      {iconKey ? (
        <View style={styles.icon}>
          <Icon iconKey={iconKey} iconSize="XS" iconColor={contentColor} />
        </View>
      ) : null}

      {text ? (
        <BodyXSText
          style={[styles.text, { color: contentColor }]}
          numberOfLines={1}
          ellipsizeMode="tail"
          testID="tag"
        >
          {text}
        </BodyXSText>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    alignItems: 'center',
    alignSelf: 'flex-start',
    borderRadius: 4,
    flexDirection: 'row',
    flexShrink: 1,
    gap: 4,
    justifyContent: 'center',
    maxWidth: 192,
    minHeight: 20,
    minWidth: 24,
    paddingHorizontal: theme.spacing.xs,
    paddingVertical: 2,
  },
  icon: {
    height: 12,
    width: 12,
  },
  text: {
    flexShrink: 1,
    textAlign: 'center',
  },
});
